import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { HolderOutlined, MenuOutlined } from '@ant-design/icons'
import type { DragEndEvent } from '@dnd-kit/core'
import { DndContext } from '@dnd-kit/core'
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button, Form, Input, Table } from 'antd'
import { SLowcodeList } from 'shj-components'

interface DataType {
  key: string
  name: string
  age: number
  address: string
}

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void
  listeners?: SyntheticListenerMap
}

const RowContext = React.createContext<RowContextProps>({})

const DragHandle: React.FC = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext)
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: 'move' }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  )
}

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string
}

const Row: React.FC<RowProps> = props => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props['data-row-key'] })

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 990 } : {}),
  }

  const contextValue = useMemo<RowContextProps>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners]
  )

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  )
}

const DragEditTable: React.FC = (props: any) => {
  const { columns, dataSource, setDataSource, showSortTitle = true, antdProps, sortTable } = props
  //   添加拖拽列
  if (columns.length > 0) {
    columns.unshift({
      title: showSortTitle ? '排序' : '',
      dataIndex: 'sort',
      width: showSortTitle ? 80 : 40,
      render: () => <DragHandle />,
    })
  }

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setDataSource(prevState => {
        const activeIndex = prevState.findIndex(record => record.key === active?.id)
        const overIndex = prevState.findIndex(record => record.key === over?.id)
        sortTable?.(arrayMove(prevState, activeIndex, overIndex))
        return arrayMove(prevState, activeIndex, overIndex)
      })
    }
  }

  return (
    <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
      <SortableContext items={dataSource.map(i => i.key)} strategy={verticalListSortingStrategy}>
        <SLowcodeList
          searchMode={'group'}
          tableType={'antd'}
          request={{}}
          rowKey="key"
          config={{ rowSelection: false, pagination: false, columns }}
          components={{ body: { row: Row } }}
          dataSource={dataSource}
          {...antdProps}
        />
      </SortableContext>
    </DndContext>
  )
}

export default DragEditTable
